import { useEffect, useState } from "react"
import { Box, Button, Grid, Icon, Typography, useTheme } from "@mui/material"

import animationHourGlass from "../../../../../../assets/animation/hourglass.json"
import animationWallet from "../../../../../../assets/animation/wallet.json"


import Lottie from 'react-lottie'
import { CgFileDocument } from "react-icons/cg"
import { BsCheck2Circle } from "react-icons/bs"
import { RiErrorWarningFill } from "react-icons/ri"
import { ValueToPaid, ValueToPaidDaily } from "../valueToPaid"
import { Parcels } from "../parcels"
import { ModalSimulator } from "../modal/simulator"
import useAuth, { IParcels } from "../../../../../../context/auth"
import moment from "moment"
import { Link, useNavigate } from "react-router-dom"
import { ModalReferAndWin } from "../modal/referAndWin"
import { Constants, Format, getTimeRemainingUntilCancellation } from "../../../../../../utils"
import { LineComponent } from "../../../../../../components/line"
import { Timer } from "../../../../../../components/timer"
import { ModalOfflineNotice } from "../modal/offlineNotice"

type TConditions = "none" | "analysis" | "paid" | "daily" | "pending"

const hourglassOptions = {
    loop: true,
    autoplay: true,
    animationData: animationHourGlass,
    rendererSettings: {
        preserveAspectRatio: 'xMidYMid slice',
    },
};

const walletOptions = {
    loop: true,
    autoplay: true,
    animationData: animationWallet,
    rendererSettings: {
        preserveAspectRatio: 'xMidYMid slice',
    },
};

export const MyRent = ({ details, openModalBusinessActivity }: { details?: boolean, openModalBusinessActivity?: () => void }) => {
    const { palette } = useTheme()
    const { user, setTab, getData } = useAuth()
    const navigate = useNavigate()

    const [condition, setCondition] = useState<TConditions>("none")
    const [openSimulator, setOpenSimulator] = useState(false)
    const [openReferAndWin, setOpenReferAndWin] = useState(false)
    const [openOfflineNotice, setOpenOfflineNotice] = useState(false)

    const blocked = (user?.client?.blockedReason ?? [])
        .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
        .find((item) => item.action === "dont_can_start_new_contract")?.message
    const [parcelsSelected, setParcelsSelected] = useState<number[]>([])

    const [parcels, setParcels] = useState<IParcels[]>([])

    const valueToPaid = parcels.filter((item) => parcelsSelected.includes(item.index)).reduce((prev, acc) => prev + acc.total, 0)

    const timeRemaining = getTimeRemainingUntilCancellation(user?.actualContract?.contract?.createdAt as string)

    useEffect(() => {
        if (parcelsSelected.length > 0 && condition === "pending" && !details) {
            window.scrollTo({
                top: document.body.scrollHeight,
                behavior: "smooth",
            })
        }
    }, [parcelsSelected, condition, details])

    useEffect(() => {
        if (user) {
            if (user?.actualContract) {
                if ([1, 5, 10].includes(user?.actualContract?.contract?.status)) {
                    setCondition("analysis")
                } else if (user?.actualContract?.contract?.status === 50) {
                    setCondition("none")
                } else {
                    if (user?.actualContract?.parcels.find((item) => item.status === 5)) {
                        setCondition("pending")
                        setParcelsSelected(parcels.filter((item) => [2, 5].includes(item.status)).map((item) => item.index))
                    } else if (user?.actualContract?.parcels.find((item) => item.status === 2)) {
                        setCondition("daily")
                    } else {
                        setCondition("paid")
                    }
                }
                setParcels(user?.actualContract?.parcels)
            } else {
                setCondition("none")
            }
        }
    }, [user, parcels])

    const timeToAnalysis = (createdAt?: string): string => {
        let holidays = [
            "01/01/2025", "18/04/2025", "21/04/2025", "01/05/2025", "19/06/2025",
            "07/09/2025", "12/10/2025", "02/11/2025", "15/11/2025", "20/11/2025", "25/12/2025",
            "01/01/2026", "03/04/2026", "21/04/2026", "01/05/2026", "04/06/2026",
            "07/09/2026", "12/10/2026", "02/11/2026", "15/11/2026", "20/11/2026", "25/12/2026"
        ]

        const days: { [key: number]: string } = {
            0: "domingo", 1: "segunda-feira", 2: "terça-feira", 3: "quarta-feira",
            4: "quinta-feira", 5: "sexta-feira", 6: "sábado"
        }

        let now = moment()
        let contractCreatedAt = createdAt ? moment(createdAt) : now.clone() // Se `createdAt` for inválido, usa `now`

        if (!contractCreatedAt.isValid()) {
            return "Erro: Data inválida."
        }

        let day = contractCreatedAt.day()
        let hour = contractCreatedAt.hour()
        let minutes = contractCreatedAt.minute()

        const isWeekend = (day: number) => day === 0 || day === 6
        const isHoliday = (date: moment.Moment) => holidays.includes(date.format("DD/MM/YYYY"))

        const nextBusinessDay = (date: moment.Moment) => {
            let nextDate = date.clone()
            do {
                nextDate.add(1, "day")
            } while (isWeekend(nextDate.day()) || isHoliday(nextDate))
            return nextDate
        }

        if (day >= 1 && day <= 5) { // Segunda a sexta
            if (hour < 8) {
                return `09:00 hrs de ${now.isSame(contractCreatedAt, "day") ? "hoje" : days[day]}`
            } else if (hour >= 8 && hour < 17) {
                return `${hour + 1}:${minutes.toString().padStart(2, "0")} hrs de ${now.isSame(contractCreatedAt, "day") ? "hoje" : days[day]}`
            }
        } else if (day === 6) { // Sábado
            if (hour < 8) {
                return `09:00 hrs de ${now.isSame(contractCreatedAt, "day") ? "hoje" : days[day]}`
            } else if (hour >= 8 && hour < 11) {
                return `${hour + 1}:${minutes.toString().padStart(2, "0")} hrs de ${now.isSame(contractCreatedAt, "day") ? "hoje" : days[day]}`
            }
        }

        // Fora do horário útil: buscar o próximo dia útil às 09:00
        let nextDay = nextBusinessDay(contractCreatedAt)
        return `09:00 hrs de ${nextDay.isSame(now, "day") ? "hoje" : days[nextDay.day()]}`
    }

    return (
        <>
            {!details && <Typography fontSize={"2rem"} fontWeight={600}>Minhas diárias</Typography>}

            {details && <Typography fontSize={"1.6rem"} fontWeight={600} p={0} m={"-2rem 0px"}>Diárias</Typography>}

            {condition === "none" &&
                <Grid
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        mt: "2rem",
                        gap: "1rem"
                    }}
                >
                    <Typography variant="body1" fontWeight={600} sx={{ opacity: .5 }} textAlign={"center"}>Você ainda não solicitou seu crédito, clique no botão abaixo para solicitar agora!</Typography>
                    <Button
                        variant="contained"
                        sx={{
                            width: "26.7rem",
                            height: "4.9rem",
                            display: "flex",
                            flexDirection: "column",
                            zIndex: 2,
                            animation: "pulse 2s infinite"

                        }}
                        onClick={() => {
                            setOpenOfflineNotice(true)
                            // if (moment(user?.client?.lastUpdate).isBefore(Constants.activityDeployedAt) && openModalBusinessActivity) {
                            //     openModalBusinessActivity()
                            // } else if (blocked || user?.client?.actualTable === 0 || moment(user?.client?.canRequestAfter).diff(moment(), "days") > 0) {
                            //     setOpenReferAndWin(true)
                            // } else {
                            //     setOpenSimulator(true)
                            // }
                        }}
                    >
                        <Typography sx={{
                            fontSize: "1.4rem",
                            fontWeight: "500",
                        }}>
                            Solicite Seu <u><strong>Crédito</strong></u> Aqui!
                        </Typography>
                    </Button>

                    <div style={{ width: "24.1rem", height: 'auto', margin: "-6rem 0rem", zIndex: 1 }}>
                        <Lottie options={walletOptions} />
                    </div>
                </Grid >
            }

            {
                condition === "analysis" &&
                <Grid
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        mt: "2rem",
                        gap: "1rem"
                    }}
                >

                    <div style={{ width: 100, height: 'auto', marginBottom: "-2rem" }}>
                        <Lottie options={hourglassOptions} />
                    </div>

                    <Typography variant="body1" fontWeight={700} color="primary" mb={"1rem"}>
                        Solicitação em análise
                    </Typography>

                    <Typography variant="body1" fontWeight={400} sx={{ textAlign: "center", maxWidth: "300px" }}>Sua soliticação de crédito será analisada até às {timeToAnalysis(user?.actualContract?.contract?.createdAt)} (durante a análise entraremos em contato por <Link target="_blank" to={`https://wa.me/+5555997181093?text=${Format.whatsAppMessage("Olá, gostaria de atendimento")}`}>Whatsapp</Link>)</Typography>
                    {user?.client?.needSendSerasa && <>
                        <br />
                        <Typography variant="body1" color="warning" fontWeight={700} textAlign={"center"} onClick={() => {
                            navigate("/cliente/serasa")
                        }}>
                            <u>PENDÊNCIA</u> <br />
                            <u>Aguardando documento!</u> <br />
                            <br />
                            Para concluir a análise de crédito, {" "}
                            <u style={{ color: palette.primary.main }}>Clique aqui</u> para enviar o PRINT da sua consulta de pendência no SERASA.
                        </Typography>
                        <Box display={"flex"} alignItems={"center"} justifyContent={"center"} flexDirection={"column"} sx={{ bgcolor: "rgba(1,1,1,0.1)" }} p={2} borderRadius={2}>
                            <Typography>Tempo restante para envio:</Typography>
                            <Timer
                                hours={timeRemaining}
                                onTimerZero={async () => {
                                    await getData()
                                }}
                                format="MMm SSs"
                                withDelay={5}
                            />
                        </Box>
                    </>}

                    <LineComponent />
                    <Button
                        variant="contained"
                        sx={{ height: "4rem", maxWidth: "275px" }}
                        onClick={() => setTab(1)}
                    >
                        Indique e ganhe
                    </Button>
                </Grid>
            }

            {
                condition === "paid" &&
                <Grid
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        mt: "2rem",
                        gap: "1rem"
                    }}
                >
                    {!details &&
                        <Button
                            variant="outlined"
                            startIcon={<CgFileDocument size={20} />}
                            sx={{
                                width: "100%",
                                minHeight: "4rem"
                            }}
                            onClick={() => navigate("/cliente/locacao")}
                        >
                            Mais detalhes
                        </Button>
                    }

                    <Icon
                        sx={{
                            width: "40.79px",
                            height: "37.88px",
                            mt: 2
                        }}
                    >
                        <BsCheck2Circle size={41} color={palette.primary.main} />
                    </Icon>

                    <Typography variant="body1" fontWeight={600}>Suas diárias estão em dia!</Typography>

                    <Typography variant="body1" fontWeight={400} sx={{ opacity: 0.5 }}>Próxima diária:</Typography>

                    <Box
                        sx={{
                            width: "100%",
                            backgroundColor: "#FFFFFF",
                            borderRadius: "10px",
                            height: "45px",
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "0px 1.5rem",
                            boxSizing: "border-box",
                            border: "none"
                        }}
                        component={"button"}
                    >
                        <Typography variant="body1" fontWeight={600}>{user?.actualContract?.parcels.find((item) => item.status === 1)?.index} de {user?.actualContract?.parcels?.length}</Typography>
                        <Typography fontSize={"1.2rem"} fontWeight={400} sx={{ opacity: 0.5 }} textAlign={"center"}>Venc.: {moment(user?.actualContract?.parcels.find((item) => item.status === 1)?.dueAt).format("DD/MM/YY")}<br />{moment(user?.actualContract?.parcels.find((item) => item.status === 1)?.dueAt).format("[(]dddd[)]")}</Typography>
                        <Typography variant="body1" fontWeight={600} color="primary">{Format.currency(user?.actualContract?.parcels.find((item) => item.status === 1)?.total ?? 0)}</Typography>
                    </Box>

                </Grid>
            }

            {
                condition === "daily" &&
                <Grid
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        mt: "2rem",
                        gap: "1rem"
                    }}
                >
                    {!details &&
                        <Button
                            variant="outlined"
                            startIcon={<CgFileDocument size={20} />}
                            sx={{
                                width: "100%",
                                minHeight: "4rem"
                            }}
                            onClick={() => navigate("/cliente/locacao")}
                        >
                            Mais detalhes
                        </Button>
                    }

                    <ValueToPaidDaily
                        value={user?.actualContract?.parcels.find((item) => item.status === 2)?.total as number}
                        dueDate={user?.actualContract?.parcels.find((item) => item.status === 2)?.dueAt as string}
                        parcel={user?.actualContract?.parcels.find((item) => item.status === 2)?.index as number}
                        totalParcels={user?.actualContract?.parcels?.length as number}
                    />
                </Grid>
            }

            {
                condition === "pending" &&
                <Grid
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        mt: "2rem",
                        gap: "1.5rem",
                        mb: parcelsSelected.length > 0 ? "7rem" : 0
                    }}
                >
                    {!details &&
                        <Button
                            variant="outlined"
                            startIcon={<CgFileDocument size={20} />}
                            sx={{
                                width: "100%",
                                minHeight: "4rem"
                            }}
                            onClick={() => navigate("/cliente/locacao")}
                        >
                            Mais detalhes
                        </Button>
                    }

                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            flexWrap: "nowrap",
                            width: "100%",
                            height: "auto",
                            backgroundColor: "rgba(218, 23, 23, 0.15)",
                            borderRadius: "10px",
                            padding: "1rem",
                            boxSizing: "border-box",
                            justifyContent: "space-around"
                        }}
                    >
                        <Grid>
                            <Icon
                                sx={{
                                    width: "auto",
                                    height: "auto",
                                }}
                            >
                                <RiErrorWarningFill size={35} color={"rgba(218, 23, 23, 0.9)"} />
                            </Icon>
                        </Grid>
                        <Grid
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: ".7rem"
                            }}
                        >
                            <Typography fontSize={"1.4rem"} fontWeight={600}>Você tem diária(s) em atraso</Typography>
                            <Typography fontSize={"1.4rem"} fontWeight={400} lineHeight={"1.694rem"} textAlign={"justify"}>A exclusão do CPF e CNPJ da lista de inadimplente junto ao <strong>Serasa</strong> será automática em até 21 dias após o pagamento da(s) diária(s) em atraso.</Typography>
                        </Grid>
                    </Box>

                    <Typography variant="body2" fontWeight={600} width={"100%"} textAlign={"start"}>Selecione as diárias que deseja pagar</Typography>


                    {details && <Box
                        sx={{
                            width: "100%",
                            background: "#4C81E61A",
                            minHeight: "11.4rem",
                            alignSelf: "center",
                            borderRadius: "10px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxSizing: "border-box",
                            padding: "2rem 1rem",
                        }}
                    >
                        <Typography fontSize={"1.4rem"} color="#10126C" fontStyle={"italic"} textAlign={"center"}><span style={{ color: "#FF0000", fontWeight: 700 }}>Atenção:</span> Os <strong>pagamentos</strong> das diárias devem ser realizados <strong>exclusivamente</strong> através da nossa plataforma <strong>www.gyroo.com.br/cliente</strong></Typography>
                    </Box>}
                    <Grid
                        sx={{
                            width: "calc(100% + 5rem)",
                            display: "flex",
                            flexDirection: "column",
                            padding: "0rem 2.5rem",
                            ml: "-5rem"
                        }}
                    >
                        {parcels.filter((item) => [5, 2].includes(item.status)).map((item) => {
                            return (
                                <Parcels
                                    checked={parcelsSelected.includes(item.index)}
                                    onChange={(value) => {
                                        if (value) {
                                            let index = parcels.indexOf(item)
                                            const otherPendings = parcels.filter((_item) => parcels.indexOf(_item) <= index && [2, 5].includes(_item.status) && !parcelsSelected.includes(_item.index))
                                            setParcelsSelected(prev => ([
                                                ...prev,
                                                ...otherPendings.map((i) => i.index)
                                            ]))
                                        } else {
                                            let index = parcels.indexOf(item)
                                            const otherPendings = parcels.filter((_item) => parcels.indexOf(_item) >= index).map((p) => p.index)
                                            if (index === 0) {
                                                setParcelsSelected([
                                                    ...parcelsSelected.filter((i) => !otherPendings.includes(i)),
                                                    parcels[index].index
                                                ])
                                            } else {
                                                setParcelsSelected(parcelsSelected.filter((i) => !otherPendings.includes(i)))
                                            }
                                        }
                                    }}
                                    parcel={item.index}
                                    totalParcels={parcels.length}
                                    value={item.total}
                                    pending={!moment().isSame(item.dueAt, "day")}
                                    daysLate={moment().diff(item.dueAt, "day")}
                                    dueDate={item.dueAt}
                                    originalValue={item.groosAmount}
                                    valueOfTheFine={item.totalLateCharges}
                                    disabled={false}
                                    key={`parcel-${item.index}`}
                                />
                            )
                        })}
                    </Grid>
                    {details && <>
                        <Button variant="outlined" sx={{ height: "4rem" }} onClick={() => navigate("/cliente/locacao/historico")}>Ver lista completa das diárias</Button>
                        <Grid
                            width={"100%"}
                            display={"flex"}
                            alignItems={"center"}
                            justifyContent={"center"}
                        >
                            <Button
                                sx={{
                                    height: "4rem"
                                }}
                                variant="outlined"
                                onClick={() => navigate(-1)}
                            >Voltar</Button>
                        </Grid>
                    </>}
                    {parcelsSelected.length > 0 && <ValueToPaid value={valueToPaid} parcel={[...parcelsSelected.sort((a, b) => a - b)].pop() as number} />}
                </Grid>

            }

            {
                details && <Grid
                    width={"100%"}
                    display={"flex"}
                    alignItems={"center"}
                    justifyContent={"center"}
                    flexDirection={"column"}
                >
                    {condition === "paid" && <Button variant="outlined" sx={{ height: "4rem" }} onClick={() => navigate("/cliente/locacao/historico")}>Ver lista completa das diárias</Button>}
                    {condition !== "pending" && <Button
                        sx={{
                            height: "4rem",
                            mt: "2rem"
                        }}
                        variant="outlined"
                        onClick={() => navigate(-1)}
                    >Voltar</Button>}
                </Grid>
            }

            <ModalSimulator open={openSimulator} onClose={() => setOpenSimulator(false)} />

            <ModalReferAndWin open={openReferAndWin} reason={blocked} onClose={() => setOpenReferAndWin(false)} />

            <ModalOfflineNotice open={openOfflineNotice} onClose={() => setOpenOfflineNotice(false)} />
        </>
    )
}